// MARK://dhk8/pos/lib/clients/floor
// Typed client for dhk8-pos-floor, reconciled against the published contract at
// services/dhk8-pos-floor/src/contract.ts (commit 9e141af).
//
// Routes: `${origin}/v1/floor/...`. Envelope: { ok, data } | bare T. The backend
// models a floor as a *map* (FloorMapDto) with `sections[]` + `tables[]` inline plus a
// separate `sessions` list; the POS works against the *default* map. adaptTable() maps
// the backend TableDto onto the @dockethub/ui-restaurant <FloorMapPage> shape.

import type { FloorStateDto, TableDto as UiTableDto, ActiveTableSessionDto } from '@dockethub/ui-restaurant'
import { getActiveBranchId } from '../api.js'
import { apiFetch, getTenantId, svcUrl } from '../api.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import type { PosUpdateFloorTableInput } from '../../gql/graphql.js'
import {
  PosFloorMapsQuery, PosFloorSessionsQuery, PosReservationsQuery, PosWaitlistQuery,
  PosCreateReservationMutation, PosUpdateReservationMutation, PosCancelReservationMutation,
  PosAddWaitlistMutation, PosNotifyWaitlistMutation, PosSeatWaitlistMutation,
  PosLeaveWaitlistMutation, PosMoveWaitlistMutation, PosNoShowAttemptWaitlistMutation,
  PosCreateFloorTableMutation, PosUpdateFloorTableMutation, PosDeleteFloorTableMutation,
  PosCreateFloorSectionMutation, PosDeleteFloorSectionMutation,
  PosOpenTableSessionMutation, PosCloseTableSessionMutation,
} from '../queries/floor.queries.js'
import { PREVIEW_STUB, SEED_FLOOR_STATE } from '../seed-preview.js'

// dhk8-pos-floor is reached through the ingress route /v1/dhk8-pos-floor/* → the service
// sees /v1/floor/... So the full URL is `${apiBase()}/dhk8-pos-floor/v1/floor/...`.
const BASE = svcUrl('dhk8-pos-floor', '/v1/floor')

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ── Backend DTOs (mirror of services/dhk8-pos-floor/src/contract.ts) ────────────

export interface BackendFloorSectionDto { id: string; mapId: string; name: string; color: string | null; sortOrder: number }
export interface BackendTableDto {
  id: string; mapId: string; sectionId: string | null; code: string; capacity: number
  shape: 'SQUARE' | 'ROUND' | 'RECT' | 'BOOTH'; posX: number; posY: number
  widthPx: number; heightPx: number; rotation: number
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'CLEANING' | 'UNAVAILABLE'
}
export interface BackendFloorMapDto {
  id: string; tenantId: string; branchId: string | null; name: string
  widthPx: number; heightPx: number; isDefault: boolean; active: boolean
  createdAt: string; updatedAt: string
  sections: BackendFloorSectionDto[]; tables: BackendTableDto[]
}
export interface TableSessionDto {
  id: string; tableId: string; tenantId: string; branchId: string | null; orderId: string | null
  covers: number; serverId: string; openedAt: string; closedAt: string | null; turnMinutes: number | null
  /** Lane #165 — código compartible OhSir guest (6 chars). */
  joinCode?: string | null
}
interface Paged<T> { items: T[]; total: number; page: number; limit: number }

// ── adapters ────────────────────────────────────────────────────────────────────

function adaptTable(t: BackendTableDto): UiTableDto {
  return {
    id: t.id,
    code: t.code,
    capacity: t.capacity,
    status: t.status, // backend's set ⊆ ui-restaurant's set
    posX: t.posX,
    posY: t.posY,
    widthPx: t.widthPx,
    heightPx: t.heightPx,
    shape: t.shape === 'ROUND' ? 'ROUND' : 'RECT',
    ...(t.sectionId ? { sectionId: t.sectionId } : {}),
  }
}

/**
 * OhSir: the takeout QR is modeled as a `PosTable` whose `code` starts with
 * `TAKEOUT` (convention — see `TAKEOUT_QR_PREFIX` in dhk8-pos-floor service).
 * It's a phantom mesa with capacity 0 used to host the takeout QR; we don't
 * want it cluttering the salon floor plan or the staff's table picker.
 */
function isTakeoutPhantomTable(t: BackendTableDto): boolean {
  return t.code.toUpperCase().startsWith('TAKEOUT')
}

/** Section metadata kept alongside the UI floor state so the POS plano can
 *  group tables by sala/terraza without a second fetch. */
export interface FloorSectionSummary {
  id: string
  name: string
  color: string | null
  /** Codes of tables in this section, in their map order. */
  tableCodes: string[]
}

/** FloorStateDto (UI shape) plus the backing map id (needed to persist table moves)
 *  and the section summary used to render section chips. */
export type FloorState = FloorStateDto & {
  mapId: string | null
  sections: FloorSectionSummary[]
}

// ── preview stub (VITE_API_STUB=1 — no backend) ─────────────────────────────────
// A module-level mutable copy of the seed so the /floor screen is fully interactive
// (drag positions, status changes, create/delete tables). The real code paths below
// are untouched. NOT used in real builds.
let stubFloor: FloorState = structuredClone(SEED_FLOOR_STATE)
let stubTableSeq = 1

// ── operations ──────────────────────────────────────────────────────────────────

/** Sucursal activa del usuario (selector del avatar, persistida en
 *  `dhk8_pos_active_branch`). El plano DEBE filtrarse por ella: un tenant
 *  multi-sucursal tiene un mapa default POR sucursal (clearDefaults es
 *  per-(tenant,branch)), así que pedir sin branchId devolvía los mapas de TODAS
 *  y el picker se quedaba con el más reciente — todas las terminales pintaban
 *  el plano de la misma sucursal. */
function activeBranchId(): string | null {
  try {
    // Selector de sucursal del POS (arreglo del 21-ago) primero; luego la del usuario.
    return getActiveBranchId() || useAuthStore.getState().user?.branchId || null
  } catch {
    return null
  }
}

export async function listFloorMaps(signal?: AbortSignal): Promise<BackendFloorMapDto[]> {
  // F4.1 / ADR-0009: lectura por el grafo (pos-floor, tenant del JWT). Escrituras REST.
  // `network-only`: react-query ya cachea esto (staleTime). Con el `cache-first`
  // de urql un refetch no tocaba la red y el plano no se actualizaba.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const branchId = activeBranchId()
  const res = await gqlClient.query(PosFloorMapsQuery, branchId ? { branchId } : {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.floorMaps ?? []) as BackendFloorMapDto[]
}

export async function listFloorSessions(signal?: AbortSignal): Promise<TableSessionDto[]> {
  // `network-only`: react-query ya cachea esto (staleTime). Con el `cache-first`
  // de urql un refetch no tocaba la red y el plano no se actualizaba.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const branchId = activeBranchId()
  const res = await gqlClient.query(PosFloorSessionsQuery, branchId ? { branchId } : {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.floorSessions ?? []) as TableSessionDto[]
}

/** A real tenant uuid — guards against `tn-*-preview` placeholders / empty ids
 *  leaking into a backend call (dhk8-pos-floor rejects them with "Invalid uuid"). */
const TENANT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** An empty floor (no map / no tables). FloorPage renders this as the
 *  "No hay mesas configuradas todavía." empty state — never a crude error. */
const EMPTY_FLOOR_STATE: FloorState = {
  plan: null,
  tables: [],
  activeSessions: [],
  mapId: null,
  sections: [],
}

/** The default floor map (with its tables), flattened into the <FloorMapPage> shape +
 *  active sessions. Picks `isDefault`, else the first active map. */
export async function getFloorState(signal?: AbortSignal): Promise<FloorState> {
  if (PREVIEW_STUB) return structuredClone(stubFloor)
  // No usable tenant context (yet) → render the empty floor instead of firing a
  // request with a missing/placeholder tenantId that the backend rejects as
  // "Invalid uuid". A tenant without a plano is a valid state (e.g. a provider).
  const tenantId = getTenantId()
  if (!tenantId || !TENANT_UUID_RE.test(tenantId)) return { ...EMPTY_FLOOR_STATE }
  const [maps, sessions] = await Promise.all([listFloorMaps(signal), listFloorSessions(signal).catch(() => [] as TableSessionDto[])])
  // Preferencia estricta por la sucursal activa: su default, luego cualquiera
  // suya. Sólo si no hay sucursal activa (tenant de una sola, sesión recién
  // abierta) o si ninguno de sus mapas casa —p.ej. mapas legacy con branchId
  // null, que el backend sigue devolviendo por el OR de listMaps— se cae al
  // comportamiento anterior, para no dejar el plano vacío a nadie.
  const branchId = activeBranchId()
  const ofBranch = branchId ? maps.filter((m) => m.branchId === branchId) : []
  const map =
    ofBranch.find((m) => m.isDefault && m.active) ??
    ofBranch.find((m) => m.active) ??
    maps.find((m) => m.isDefault && m.active) ??
    maps.find((m) => m.active) ??
    maps[0] ??
    null
  // Filter the takeout phantom table from the salon floor plan — it's a
  // backend convention (capacity 0, code starts with TAKEOUT) and shouldn't
  // render on /floor.
  const visibleTables = map ? map.tables.filter((t) => !isTakeoutPhantomTable(t)) : []
  const tableByIdLookup = map ? new Map(map.tables.map((t) => [t.id, t.code])) : new Map<string, string>()
  const activeSessions: ActiveTableSessionDto[] = sessions.filter((s) => !s.closedAt).map((s) => ({
    id: s.id, tableId: s.tableId, covers: s.covers, openedAt: s.openedAt, userId: s.serverId,
    orderId: s.orderId,
    tableCode: tableByIdLookup.get(s.tableId) ?? null,
    // Lane #165 — joinCode propagado para que el modal info de mesa lo muestre.
    joinCode: s.joinCode ?? null,
  }))
  // Group tables under their section so the POS can render section chips.
  const sections: FloorSectionSummary[] = map
    ? [...map.sections]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((s) => ({
          id: s.id,
          name: s.name,
          color: s.color,
          tableCodes: visibleTables.filter((t) => t.sectionId === s.id).map((t) => t.code),
        }))
    : []
  return {
    plan: map ? { id: map.id, widthPx: map.widthPx, heightPx: map.heightPx } : null,
    tables: visibleTables.map(adaptTable),
    activeSessions,
    mapId: map?.id ?? null,
    sections,
  }
}

/** Persist table positions (drag-to-arrange). One PATCH per table — the backend has no
 *  bulk endpoint. Sends posX/posY. */
export async function saveFloorTables(mapId: string, tables: UiTableDto[]): Promise<void> {
  if (PREVIEW_STUB) {
    // Replace the whole table set (positions, capacities, shapes, adds/deletes).
    stubFloor = { ...stubFloor, tables: tables.map((t) => structuredClone(t)) }
    return
  }
  await Promise.all(tables.map(async (t) => {
    const res = await gqlClient.mutation(PosUpdateFloorTableMutation, {
      mapId,
      tableId: t.id,
      input: { posX: Math.round(t.posX), posY: Math.round(t.posY) },
    }).toPromise()
    if (res.error) throw res.error
  }))
}

export interface OpenSessionInput { covers: number | string; serverId: string; orderId?: string; tenantId?: string; branchId?: string }
/** Open a floor session for a table. The backend zod schema (services/dhk8-pos-floor/src/schema.ts
 *  → OpenTableSessionSchema) requires `tenantId` (uuid) + `serverId` (uuid), accepts an optional
 *  positive-int `covers`, and optional uuid `orderId`/`branchId`. The POS used to send only
 *  `{ covers, serverId }` and an empty-string `orderId` from the dialog state — both caused a 400
 *  (bug #98). Here we (a) inject `tenantId` from getTenantId() so callers don't have to know,
 *  (b) coerce `covers` to a positive integer (the dialog binds it as a string), (c) drop empty
 *  `orderId`/`branchId` strings so they don't fail uuid validation. */
export async function openTableSession(tableId: string, input: OpenSessionInput): Promise<TableSessionDto> {
  // F9.8 / ADR-0008: escritura por el grafo. El tenant lo pone el JWT (no se envia);
  // covers se coacciona a entero positivo (el dialog lo bindea como string).
  const coversRaw = typeof input.covers === 'string' ? Number(input.covers) : input.covers
  const coversNum = Number.isFinite(coversRaw) ? Math.max(1, Math.trunc(coversRaw as number)) : 1
  const gqlInput: { serverId: string; covers: number; branchId?: string; orderId?: string } = {
    serverId: input.serverId,
    covers: coversNum,
  }
  if (input.branchId && input.branchId.length > 0) gqlInput.branchId = input.branchId
  if (input.orderId && input.orderId.length > 0) gqlInput.orderId = input.orderId
  const res = await gqlClient.mutation(PosOpenTableSessionMutation, { tableId, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.openTableSession
  if (!r) throw new Error('openTableSession: respuesta vacia')
  return r as unknown as TableSessionDto
}
export async function closeTableSession(sessionId: string, _input: { closedByUserId?: string } = {}): Promise<TableSessionDto> {
  // F9.8 / ADR-0008: escritura por el grafo. El backend cierra y deja la mesa
  // CLEANING por defecto; closedByUserId no lo consume el contrato (se ignora).
  const res = await gqlClient.mutation(PosCloseTableSessionMutation, { sessionId, input: {} }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.closeTableSession
  if (!r) throw new Error(`NOT_FOUND: session ${sessionId}`)
  return r as unknown as TableSessionDto
}

/**
 * Backend table-status set. The dhk8-pos-floor contract's `UpdateTableSchema`
 * accepts `status` and the service emits `dhk8.pos.table.status_changed`, so
 * the staff can drive the full table-state model (E1) from /floor:
 *   AVAILABLE · OCCUPIED · RESERVED · CLEANING (sucia) · UNAVAILABLE (fuera de servicio).
 * `DIRTY` is a legacy UI alias — normalize it to the backend's `CLEANING`.
 */
type BackendTableStatus = BackendTableDto['status']
function toBackendStatus(s: UiTableDto['status']): BackendTableStatus {
  return s === 'DIRTY' ? 'CLEANING' : (s as BackendTableStatus)
}
export async function setTableStatus(mapId: string, tableId: string, status: UiTableDto['status']): Promise<void> {
  if (PREVIEW_STUB) {
    stubFloor = { ...stubFloor, tables: stubFloor.tables.map((t) => (t.id === tableId ? { ...t, status } : t)) }
    return
  }
  const res = await gqlClient.mutation(PosUpdateFloorTableMutation, {
    mapId,
    tableId,
    input: { status: toBackendStatus(status) },
  }).toPromise()
  if (res.error) throw res.error
}

// ── CRUD: tables + sections (shared with the admin editor) ──────────────────
export interface CreateTableInput {
  code: string; capacity: number; posX: number; posY: number; widthPx: number; heightPx: number
  shape: 'RECT' | 'ROUND'; sectionId: string
}
export async function createTable(mapId: string, input: CreateTableInput): Promise<BackendTableDto> {
  const res = await gqlClient.mutation(PosCreateFloorTableMutation, {
    mapId,
    input: {
      code: input.code, capacity: input.capacity, posX: Math.round(input.posX), posY: Math.round(input.posY),
      widthPx: input.widthPx, heightPx: input.heightPx, shape: input.shape, sectionId: input.sectionId,
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.createFloorTable
  if (!r) throw new Error('createTable: respuesta vacia')
  return r as unknown as BackendTableDto
}
export async function deleteTable(mapId: string, tableId: string): Promise<void> {
  const res = await gqlClient.mutation(PosDeleteFloorTableMutation, { mapId, tableId }).toPromise()
  if (res.error) throw res.error
}
export async function patchTable(mapId: string, tableId: string, body: Partial<{ code: string; capacity: number; sectionId: string; shape: 'RECT' | 'ROUND'; widthPx: number; heightPx: number }>): Promise<BackendTableDto> {
  const input: PosUpdateFloorTableInput = {}
  if (body.code !== undefined) input['code'] = body.code
  if (body.capacity !== undefined) input['capacity'] = body.capacity
  if (body.sectionId !== undefined) input['sectionId'] = body.sectionId
  if (body.shape !== undefined) input['shape'] = body.shape
  if (body.widthPx !== undefined) input['widthPx'] = body.widthPx
  if (body.heightPx !== undefined) input['heightPx'] = body.heightPx
  const res = await gqlClient.mutation(PosUpdateFloorTableMutation, { mapId, tableId, input }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.updateFloorTable
  if (!r) throw new Error(`NOT_FOUND: table ${tableId}`)
  return r as unknown as BackendTableDto
}
export async function createSection(mapId: string, name: string): Promise<{ id: string; name: string }> {
  const res = await gqlClient.mutation(PosCreateFloorSectionMutation, { mapId, input: { name } }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.createFloorSection
  if (!r) throw new Error('createSection: respuesta vacia')
  return { id: r.id, name: r.name }
}
export async function deleteSection(mapId: string, sectionId: string): Promise<void> {
  const res = await gqlClient.mutation(PosDeleteFloorSectionMutation, { mapId, sectionId }).toPromise()
  if (res.error) throw res.error
}

/** Mint an id for a brand-new table created in the floor editor (preview only — a real
 *  backend assigns ids server-side via a create endpoint, not modelled in the contract). */
export function newStubTableId(): string {
  return `t-new-${Date.now().toString(36)}-${stubTableSeq++}`
}

// ── training-mode / test reset ──────────────────────────────────────────────────
export function __resetFloorStub(): void {
  stubFloor = structuredClone(SEED_FLOOR_STATE)
  stubTableSeq = 1
}

// ── reservations (Fase 1) ───────────────────────────────────────────────────

/** Lane #163. */
export type ReservationOccasion = 'birthday' | 'anniversary' | 'date' | 'business' | 'other'
export type ReservationSource = 'walk_in' | 'phone' | 'web' | 'instagram' | 'recommended' | 'other'

export interface ReservationDto {
  id: string
  tenantId: string
  branchId: string | null
  tableId: string | null
  name: string
  phone: string | null
  email: string | null
  partySize: number
  reservedAt: string
  durationMin: number | null
  status: 'CONFIRMED' | 'ARRIVED' | 'SEATED' | 'CANCELLED' | 'NO_SHOW'
  notes: string | null
  allergies: string | null
  highChair: boolean
  wheelchair: boolean
  occasion: ReservationOccasion | null
  source: ReservationSource | null
  confirmedAt: string | null
  cancelledAt: string | null
  seatedSessionId: string | null
  /** Stream 3 Fase 1: código humano-friendly único por (tenant, día). */
  reservationCode?: string | null
  reservationDay?: string | null
  createdAt: string
}

export interface CreateReservationInput {
  name: string
  phone?: string
  email?: string
  partySize: number
  reservedAt: string
  tableId?: string | null
  durationMin?: number
  notes?: string
  allergies?: string
  highChair?: boolean
  wheelchair?: boolean
  occasion?: ReservationOccasion
  source?: ReservationSource
  branchId?: string
}

function todayLocalISODate(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export async function listReservations(
  opts: { date?: string; from?: string; to?: string; branchId?: string } = {},
): Promise<ReservationDto[]> {
  // F5.1 / ADR-0009: lectura por el grafo (pos-floor, tenant del JWT). Escrituras REST.
  const vars: { branchId?: string; date?: string; from?: string; to?: string } = {}
  // Por defecto, sólo las de MI sucursal.
  const suc = opts.branchId ?? getActiveBranchId()
  if (suc) vars.branchId = suc
  if (opts.date) vars.date = opts.date
  if (opts.from) vars.from = opts.from
  if (opts.to) vars.to = opts.to
  const res = await gqlClient.query(PosReservationsQuery, vars, { requestPolicy: 'network-only' }).toPromise()
  if (res.error) throw res.error
  return (res.data?.reservations ?? []) as unknown as ReservationDto[]
}

export async function listTodayReservations(): Promise<ReservationDto[]> {
  return listReservations({ date: todayLocalISODate() })
}

export async function createReservation(input: CreateReservationInput): Promise<ReservationDto> {
  // F8.1 / ADR-0009: escritura por el grafo. El tenant lo pone el JWT (no se envia).
  const res = await gqlClient.mutation(PosCreateReservationMutation, {
    input: {
      name: input.name, partySize: input.partySize, reservedAt: input.reservedAt,
      // Hereda la sucursal activa: sin esto la reserva nacia sin sucursal y
      // aparecia en las 18 por igual.
      branchId: input.branchId ?? getActiveBranchId(), tableId: input.tableId ?? null,
      phone: input.phone ?? null, email: input.email ?? null,
      durationMin: input.durationMin ?? null, notes: input.notes ?? null,
      allergies: input.allergies ?? null, highChair: input.highChair ?? null,
      wheelchair: input.wheelchair ?? null, occasion: input.occasion ?? null, source: input.source ?? null,
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.createReservation
  if (!r) throw new Error('createReservation: respuesta vacia')
  return r as unknown as ReservationDto
}

export async function updateReservation(
  id: string,
  input: Partial<Pick<ReservationDto,
    'name' | 'phone' | 'email' | 'partySize' | 'reservedAt' | 'durationMin' |
    'notes' | 'status' | 'tableId' | 'allergies' | 'highChair' | 'wheelchair' |
    'occasion' | 'source'
  >>,
): Promise<ReservationDto> {
  const res = await gqlClient.mutation(PosUpdateReservationMutation, { id, input }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.updateReservation
  if (!r) throw new Error(`NOT_FOUND: reservation ${id}`)
  return r as unknown as ReservationDto
}

export async function cancelReservation(id: string): Promise<ReservationDto> {
  const res = await gqlClient.mutation(PosCancelReservationMutation, { id }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.cancelReservation
  if (!r) throw new Error(`NOT_FOUND: reservation ${id}`)
  return r as unknown as ReservationDto
}

// ── waitlist (Fase 1) ───────────────────────────────────────────────────────

export interface WaitlistEntryDto {
  id: string
  tenantId: string
  branchId: string | null
  name: string
  phone: string | null
  partySize: number
  status: 'WAITING' | 'NOTIFIED' | 'SEATED' | 'LEFT' | 'EXPIRED'
  notes: string | null
  estimatedWaitMin: number | null
  position: number
  /** Lane #164 — 0..3 strikes de "sin respuesta". A 3 → status=EXPIRED. */
  noShowAttempts: number
  addedAt: string
  notifiedAt: string | null
  seatedAt: string | null
  seatedSessionId: string | null
  /** Código humano-friendly de cola (ej "Q12"). Stream 3 Fase 1. */
  queueCode?: string | null
  queueDay?: string | null
}

export interface CreateWaitlistInput {
  name: string
  phone?: string
  partySize: number
  notes?: string
  estimatedWaitMin?: number
  branchId?: string
}

export async function listWaitlist(opts: { includeHistory?: boolean; branchId?: string } = {}): Promise<WaitlistEntryDto[]> {
  // F5.1 / ADR-0009: lectura por el grafo (pos-floor, tenant del JWT). Escrituras REST.
  const vars: { branchId?: string; includeHistory?: boolean } = {}
  const suc = opts.branchId ?? getActiveBranchId()
  if (suc) vars.branchId = suc
  if (opts.includeHistory) vars.includeHistory = true
  const res = await gqlClient.query(PosWaitlistQuery, vars, { requestPolicy: 'network-only' }).toPromise()
  if (res.error) throw res.error
  return (res.data?.waitlist ?? []) as unknown as WaitlistEntryDto[]
}

export async function addWaitlistEntry(input: CreateWaitlistInput): Promise<WaitlistEntryDto> {
  // F9.8 / ADR-0008: escritura por el grafo. El tenant lo pone el JWT (no se envia).
  const res = await gqlClient.mutation(PosAddWaitlistMutation, {
    input: {
      name: input.name,
      partySize: input.partySize,
      phone: input.phone ?? null,
      notes: input.notes ?? null,
      estimatedWaitMin: input.estimatedWaitMin ?? null,
      branchId: input.branchId ?? getActiveBranchId(),
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.addWaitlistEntry
  if (!r) throw new Error('addWaitlistEntry: respuesta vacia')
  return r as unknown as WaitlistEntryDto
}

export async function notifyWaitlistEntry(id: string): Promise<WaitlistEntryDto> {
  const res = await gqlClient.mutation(PosNotifyWaitlistMutation, { id }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.notifyWaitlistEntry
  if (!r) throw new Error(`NOT_FOUND: waitlist entry ${id}`)
  return r as unknown as WaitlistEntryDto
}

export async function seatWaitlistEntry(
  id: string,
  input: { tableId: string; serverId: string; covers?: number },
): Promise<WaitlistEntryDto> {
  const seatInput: { tableId: string; serverId: string; covers?: number } = {
    tableId: input.tableId,
    serverId: input.serverId,
  }
  if (input.covers !== undefined) seatInput.covers = input.covers
  const res = await gqlClient.mutation(PosSeatWaitlistMutation, { id, input: seatInput }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.seatWaitlistEntry
  if (!r) throw new Error(`NOT_FOUND: waitlist entry ${id}`)
  return r as unknown as WaitlistEntryDto
}

export async function leaveWaitlistEntry(id: string): Promise<WaitlistEntryDto> {
  const res = await gqlClient.mutation(PosLeaveWaitlistMutation, { id }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.leaveWaitlistEntry
  if (!r) throw new Error(`NOT_FOUND: waitlist entry ${id}`)
  return r as unknown as WaitlistEntryDto
}

/** Lane #164 — reorder por taps (subir/bajar prioridad sin drag). */
export async function moveWaitlistEntry(id: string, direction: 'up' | 'down'): Promise<WaitlistEntryDto> {
  const res = await gqlClient.mutation(PosMoveWaitlistMutation, { id, direction }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.moveWaitlistEntry
  if (!r) throw new Error(`NOT_FOUND: waitlist entry ${id}`)
  return r as unknown as WaitlistEntryDto
}

/** Lane #164 — incrementa contador "sin respuesta". Al 3er tap marca EXPIRED. */
export async function attemptNoShowWaitlistEntry(id: string): Promise<WaitlistEntryDto> {
  const res = await gqlClient.mutation(PosNoShowAttemptWaitlistMutation, { id }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.noShowAttemptWaitlistEntry
  if (!r) throw new Error(`NOT_FOUND: waitlist entry ${id}`)
  return r as unknown as WaitlistEntryDto
}

/** wa.me deep-link builder — local-only, no backend. */
export function waMeLink(phone: string, message: string): string {
  const digits = phone.replace(/[^0-9]/g, '')
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
}

// ── OhSir guest notifications (panel de notificaciones del POS) ──────────────
// Notificaciones categorizadas que el comensal manda desde la PWA OhSir.
// Mirror de services/dhk8-pos-floor/src/services/guest-notifications.service.ts.

export type GuestNotificationTopic =
  | 'CALL_WAITER'
  | 'REQUEST_CHECK'
  | 'REQUEST_ITEM'
  | 'REPORT_ISSUE'
  | 'OTHER'

export type GuestNotificationStatus = 'PENDING' | 'ACKNOWLEDGED' | 'RESOLVED'

export interface GuestNotificationDto {
  id: string
  tenantId: string
  branchId: string | null
  tableSessionId: string
  tableId: string
  tableCode: string | null
  topic: GuestNotificationTopic
  note: string | null
  status: GuestNotificationStatus
  resolvedBy: string | null
  createdAt: string
  acknowledgedAt: string | null
  resolvedAt: string | null
}

/** Lista las notificaciones del tenant. status omitido = activas (PENDING+ACKNOWLEDGED). */
export async function listGuestNotifications(
  opts: { status?: GuestNotificationStatus; branchId?: string; limit?: number } = {},
): Promise<{ items: GuestNotificationDto[]; total: number }> {
  const tenantId = getTenantId() ?? ''
  const params = new URLSearchParams({ tenantId })
  if (opts.status) params.set('status', opts.status)
  if (opts.branchId) params.set('branchId', opts.branchId)
  if (opts.limit) params.set('limit', String(opts.limit))
  return fetchData<{ items: GuestNotificationDto[]; total: number }>(
    `${BASE}/guest-notifications?${params.toString()}`,
  )
}

/** El staff cambia el estado: ACKNOWLEDGED (vista) o RESOLVED (atendida). */
export async function updateGuestNotificationStatus(
  id: string,
  status: GuestNotificationStatus,
): Promise<GuestNotificationDto> {
  return fetchData<GuestNotificationDto>(
    `${BASE}/guest-notifications/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: { status } },
  )
}

/**
 * E2 — indicador de notificación por mesa en /floor.
 *
 * Devuelve las notificaciones PENDIENTES agrupadas por `tableId`, para que el
 * plano pinte un badge con conteo en cada mesa con avisos del comensal sin
 * tocar. Es monitoreo rápido — el panel completo sigue siendo /avisos.
 *
 * Sólo PENDING: una notificación ya "vista" (ACKNOWLEDGED) deja de marcar la
 * mesa en el plano para no saturar al staff; sigue visible en /avisos.
 */
export async function getPendingNotificationsByTable(
  branchId?: string,
): Promise<Map<string, GuestNotificationDto[]>> {
  const byTable = new Map<string, GuestNotificationDto[]>()
  if (PREVIEW_STUB) return byTable
  const tenantId = getTenantId()
  if (!tenantId || !TENANT_UUID_RE.test(tenantId)) return byTable
  const opts: { status: GuestNotificationStatus; limit: number; branchId?: string } = {
    status: 'PENDING',
    limit: 200,
  }
  if (branchId) opts.branchId = branchId
  const r = await listGuestNotifications(opts).catch(() => ({ items: [] as GuestNotificationDto[], total: 0 }))
  for (const n of r.items) {
    const list = byTable.get(n.tableId)
    if (list) list.push(n)
    else byTable.set(n.tableId, [n])
  }
  return byTable
}
